import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { format, addDays, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  clientInvoiceGenerationService,
  CompletedShiftForInvoice,
  ClientInvoice,
  GenerateClientInvoiceRequest,
  INVOICE_STATUS_LABELS,
  SERVICE_TYPE_LABELS,
  SERVICE_TYPES,
  BillingRate,
  ClientBaseRate,
  FlatRate,
  billingRatesService,
  clientBaseRateService,
  flatRateService,
} from '../../services/newBillingService';
import { clientService, Client } from '../../services/clientService';
import { useAuth } from '../../contexts/AuthContext';
import PageLayout from '@/components/Layout/PageLayout';

const STATUS_COLORS: Record<number, 'default' | 'warning' | 'info' | 'success' | 'error' | 'secondary'> = {
  1: 'default',
  2: 'warning',
  3: 'info',
  4: 'success',
  5: 'error',
  6: 'default',
  7: 'secondary',
};

function fmtDate(iso: string) {
  try { return format(parseISO(iso), 'dd MMM yyyy'); } catch { return iso; }
}
function fmtTime(iso?: string) {
  if (!iso) return '-';
  try { return format(parseISO(iso), 'HH:mm'); } catch { return iso; }
}
function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}
function getDayType(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDay();
  if (day === 6) return 'Saturday';
  if (day === 0) return 'Sunday';
  return 'Weekday';
}
function clientDisplayName(c: Client) {
  const name = (c.fullName || `${c.firstName ?? ''} ${c.lastName ?? ''}`).trim();
  return name || `Client #${c.id}`;
}

function minutesToTime(m: number) {
  if (m >= 1440) return '24:00';
  return `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
}

interface InvoiceSegmentSlice {
  name: string;
  hours: number;
  rate: number;
  amount: number;
  /** "Hourly" | "Sleepover" | "Travel" — controls how the slice is summarized in the UI. */
  kind?: 'Hourly' | 'Sleepover' | 'Travel';
}

interface ShiftEstimate {
  total: number;
  segments: InvoiceSegmentSlice[];
  uncoveredHours: number;
  /** Reason the estimate is unavailable / partial (e.g. missing FlatRate config). */
  warning?: string;
}

function getDayTypeFromDate(d: Date): string {
  const day = d.getDay();
  if (day === 6) return 'Saturday';
  if (day === 0) return 'Sunday';
  return 'Weekday';
}

interface EstimateInputs {
  startIso?: string;
  endIso?: string;
  baseRate: number;
  hourlyRates: BillingRate[];
  isSleepover: boolean;
  travelKm: number;
  sleepoverFlatRate: FlatRate | null;
  travelFlatRate: FlatRate | null;
}

/**
 * Mirrors the backend's `BuildLineItems` so the page can render the time-of-day
 * pricing the user is about to be billed without trusting whatever the backend
 * sends back as `estimatedAmount` (which has been observed to fall back to a
 * flat base rate when its rate-segment lookup is mis-seeded). Also handles
 * sleepover (flat per-night) and travel (per-km) shift add-ons.
 */
function computeShiftEstimate(inputs: EstimateInputs): ShiftEstimate {
  const empty: ShiftEstimate = { total: 0, segments: [], uncoveredHours: 0 };
  const { startIso, endIso, baseRate, hourlyRates, isSleepover, travelKm,
          sleepoverFlatRate, travelFlatRate } = inputs;
  if (!startIso || !endIso) return empty;
  const start = parseISO(startIso);
  const end = parseISO(endIso);
  if (!(end > start)) return empty;

  const slices: InvoiceSegmentSlice[] = [];
  let warning: string | undefined;
  let uncoveredHours = 0;

  if (isSleepover) {
    if (sleepoverFlatRate) {
      const amount = Math.round(sleepoverFlatRate.unitRate * 100) / 100;
      slices.push({
        name: 'Sleepover (Inactive Overnight)',
        hours: 1,
        rate: sleepoverFlatRate.unitRate,
        amount,
        kind: 'Sleepover',
      });
    } else {
      warning = 'Sleepover shift but no active Sleepover ClientCharge FlatRate is configured (Billing Rates → Special Rates).';
    }
  } else {
    if (baseRate <= 0 || hourlyRates.length === 0) return empty;
    const totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const dayType = getDayTypeFromDate(start);
    const daySegments = hourlyRates.filter(r => r.dayType === dayType);
    if (daySegments.length === 0) {
      return { ...empty, uncoveredHours: Math.round(totalHours * 100) / 100 };
    }

    type Norm = { name: string; start: number; end: number; pct: number };
    const normalized: Norm[] = [];
    for (const seg of daySegments) {
      if (seg.timeEndMinutes <= seg.timeStartMinutes) {
        normalized.push({ name: seg.name, start: seg.timeStartMinutes, end: 1440, pct: seg.percentage });
        normalized.push({ name: seg.name, start: 0, end: seg.timeEndMinutes, pct: seg.percentage });
      } else {
        normalized.push({ name: seg.name, start: seg.timeStartMinutes, end: seg.timeEndMinutes, pct: seg.percentage });
      }
    }

    const shiftStartMin = start.getHours() * 60 + start.getMinutes();
    const shiftEndMinRaw = end.getHours() * 60 + end.getMinutes();
    const sameDay = start.toDateString() === end.toDateString();
    const overnight = !sameDay || shiftEndMinRaw <= shiftStartMin;
    const effectiveEnd = overnight ? shiftEndMinRaw + 1440 : shiftEndMinRaw;

    for (const seg of [...normalized].sort((a, b) => a.start - b.start)) {
      let segStart = seg.start;
      let segEnd = seg.end;
      if (overnight && segStart < shiftStartMin) { segStart += 1440; segEnd += 1440; }
      const overlapStart = Math.max(shiftStartMin, segStart);
      const overlapEnd = Math.min(effectiveEnd, segEnd);
      if (overlapEnd <= overlapStart) continue;
      const hours = Math.round((overlapEnd - overlapStart) / 60 * 10000) / 10000;
      const rate = Math.round(baseRate * seg.pct / 100 * 100) / 100;
      const amount = Math.round(hours * rate * 100) / 100;
      slices.push({ name: seg.name, hours, rate, amount, kind: 'Hourly' });
    }

    const coveredHours = slices.reduce((s, x) => s + x.hours, 0);
    uncoveredHours = Math.max(0, Math.round((totalHours - coveredHours) * 100) / 100);
  }

  if (travelKm > 0) {
    if (travelFlatRate) {
      const km = Math.round(travelKm * 100) / 100;
      const amount = Math.round(km * travelFlatRate.unitRate * 100) / 100;
      slices.push({
        name: `Provider Travel (${km.toFixed(2)} km)`,
        hours: km,
        rate: travelFlatRate.unitRate,
        amount,
        kind: 'Travel',
      });
    } else {
      warning = (warning ? warning + ' ' : '') + 'Travel km recorded but no active Transport ClientCharge FlatRate is configured.';
    }
  }

  const total = Math.round(slices.reduce((s, x) => s + x.amount, 0) * 100) / 100;
  return { total, segments: slices, uncoveredHours, warning };
}

const ClientInvoicesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [includeBilled, setIncludeBilled] = useState(false);

  const [shifts, setShifts] = useState<CompletedShiftForInvoice[]>([]);
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<number>>(new Set());
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsError, setShiftsError] = useState<string | null>(null);

  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [currentClientId, setCurrentClientId] = useState<number | null>(null);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [serviceType, setServiceType] = useState<string>('PersonalCare');
  const [dueDate, setDueDate] = useState<string>(format(addDays(new Date(), 14), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const [detailInvoice, setDetailInvoice] = useState<ClientInvoice | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [statusInvoice, setStatusInvoice] = useState<ClientInvoice | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<number>(1);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [clientChargeRates, setClientChargeRates] = useState<BillingRate[]>([]);
  const [clientBaseRates, setClientBaseRates] = useState<ClientBaseRate[]>([]);
  const [sleepoverFlatRate, setSleepoverFlatRate] = useState<FlatRate | null>(null);
  const [travelFlatRate, setTravelFlatRate] = useState<FlatRate | null>(null);
  const [ratesLoading, setRatesLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setClientsLoading(true);
      try {
        const res: any = await clientService.getClients({ pageSize: 500, sortBy: 'firstName' });
        const list: Client[] = Array.isArray(res)
          ? res
          : (res?.results ?? res?.items ?? res?.data ?? res?.clients ?? []);
        if (mounted) setClients(list);
      } catch (err) {
        console.error('Failed to load clients', err);
      } finally {
        if (mounted) setClientsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setRatesLoading(true);
      try {
        const [hourly, flat] = await Promise.all([
          billingRatesService.getAllRates('ClientCharge'),
          flatRateService.getAll(undefined, 'ClientCharge'),
        ]);
        if (!mounted) return;
        if (hourly.success && hourly.data) {
          setClientChargeRates(hourly.data.filter(r => r.isActive));
        }
        if (flat.success && flat.data) {
          const today = new Date();
          const isActive = (r: FlatRate) => r.isActive
            && new Date(r.effectiveFrom) <= today
            && (!r.effectiveTo || new Date(r.effectiveTo) >= today);
          setSleepoverFlatRate(flat.data.find(r => r.category === 'Sleepover' && isActive(r)) ?? null);
          setTravelFlatRate(flat.data.find(r => r.category === 'Transport' && isActive(r)) ?? null);
        }
      } catch (err) {
        console.error('Failed to load ClientCharge rates', err);
      } finally {
        if (mounted) setRatesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedClient) {
      setClientBaseRates([]);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await clientBaseRateService.getForClient(selectedClient.id);
        if (mounted && res.success && res.data) {
          setClientBaseRates(res.data.filter(r => r.isActive));
        } else if (mounted) {
          setClientBaseRates([]);
        }
      } catch (err) {
        console.error('Failed to load client base rates', err);
        if (mounted) setClientBaseRates([]);
      }
    })();
    return () => { mounted = false; };
  }, [selectedClient]);

  const activeBaseRate = clientBaseRates.find(r => r.serviceType === serviceType) ?? null;
  const segmentsForServiceType = clientChargeRates;

  const loadInvoices = useCallback(async (cid: number) => {
    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const res = await clientInvoiceGenerationService.getInvoices(cid);
      if (res.success && res.data) {
        setInvoices(res.data);
      } else {
        setInvoicesError(res.message || 'Failed to load invoices.');
      }
    } catch (e: any) {
      setInvoicesError(e.message);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  const loadShifts = useCallback(async () => {
    if (!selectedClient) {
      setShiftsError('Please select a client.');
      return;
    }
    const cid = selectedClient.id;
    setShiftsLoading(true);
    setShiftsError(null);
    setShifts([]);
    setSelectedShiftIds(new Set());
    try {
      const res = await clientInvoiceGenerationService.getCompletedShifts(
        cid,
        startDate || undefined,
        endDate || undefined,
        includeBilled
      );
      if (res.success && res.data) {
        setShifts(res.data);
        setCurrentClientId(cid);
        loadInvoices(cid);
      } else {
        setShiftsError(res.message || 'No data returned.');
      }
    } catch (e: any) {
      setShiftsError(e.message);
    } finally {
      setShiftsLoading(false);
    }
  }, [selectedClient, startDate, endDate, includeBilled, loadInvoices]);

  const unbilledShifts = shifts.filter(s => !s.isBilled);
  const unbilledCount = unbilledShifts.length;

  const handleSelectAll = () => {
    if (selectedShiftIds.size === unbilledCount) {
      setSelectedShiftIds(new Set());
    } else {
      setSelectedShiftIds(new Set(unbilledShifts.map(s => s.shiftId)));
    }
  };

  const handleToggle = (shiftId: number) => {
    setSelectedShiftIds(prev => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId); else next.add(shiftId);
      return next;
    });
  };

  const shiftEstimates = React.useMemo(() => {
    const map = new Map<number, ShiftEstimate>();
    for (const s of shifts) {
      const startIso = s.actualStartTime ?? s.startDateTime;
      const endIso = s.actualEndTime ?? s.endDateTime;
      map.set(s.shiftId, computeShiftEstimate({
        startIso,
        endIso,
        baseRate: activeBaseRate?.baseHourlyRate ?? 0,
        hourlyRates: segmentsForServiceType,
        isSleepover: !!s.isSleepover,
        travelKm: typeof s.travelKilometres === 'number' ? s.travelKilometres : 0,
        sleepoverFlatRate,
        travelFlatRate,
      }));
    }
    return map;
  }, [shifts, activeBaseRate, segmentsForServiceType, sleepoverFlatRate, travelFlatRate]);

  const selectedShifts = shifts.filter(s => selectedShiftIds.has(s.shiftId));
  const totalSelectedHours = selectedShifts.reduce((sum, s) => sum + (Number(s.totalHours) || 0), 0);
  const totalSelectedAmount = selectedShifts.reduce(
    (sum, s) => sum + (shiftEstimates.get(s.shiftId)?.total ?? 0),
    0,
  );

  const openGenerateDialog = () => {
    setGenerateError(null);
    setGenerateOpen(true);
  };

  const handleGenerate = async () => {
    if (selectedShiftIds.size === 0 || !selectedClient) return;
    const sortedDates = selectedShifts.map(s => s.shiftDate).sort();
    const request: GenerateClientInvoiceRequest = {
      clientId: selectedClient.id,
      clientName: clientDisplayName(selectedClient),
      shiftIds: Array.from(selectedShiftIds),
      serviceType,
      periodStart: sortedDates[0],
      periodEnd: sortedDates[sortedDates.length - 1],
      dueDate: dueDate || undefined,
      notes: notes || undefined,
      createdBy: (user as any)?.name || (user as any)?.email || 'system',
    };
    setGenerating(true);
    setGenerateError(null);
    try {
      const res = await clientInvoiceGenerationService.generateInvoice(request);
      if (res.success && res.data) {
        setGenerateOpen(false);
        setSelectedShiftIds(new Set());
        setSuccessMsg(`Invoice ${res.data.invoiceNumber} generated successfully.`);
        await loadShifts();
      } else {
        setGenerateError(res.message || 'Failed to generate invoice.');
      }
    } catch (e: any) {
      setGenerateError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const openStatusDialog = (inv: ClientInvoice) => {
    setStatusInvoice(inv);
    setNewStatus(inv.status);
    setStatusOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusInvoice) return;
    setStatusUpdating(true);
    try {
      const res = await clientInvoiceGenerationService.updateStatus(statusInvoice.id, newStatus);
      if (res.success) {
        setStatusOpen(false);
        setSuccessMsg('Invoice status updated.');
        if (currentClientId) loadInvoices(currentClientId);
      }
    } catch {
      // stay open
    } finally {
      setStatusUpdating(false);
    }
  };

  const openDeleteDialog = (id: number) => { setDeleteId(id); setDeleteOpen(true); };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await clientInvoiceGenerationService.deleteInvoice(deleteId);
      setDeleteOpen(false);
      setSuccessMsg('Invoice deleted. The shifts are now available to invoice again.');
      if (currentClientId) {
        loadInvoices(currentClientId);
        await loadShifts();
      }
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = async (inv: ClientInvoice) => {
    try {
      const res = await clientInvoiceGenerationService.getInvoiceById(inv.id);
      setDetailInvoice(res.success && res.data ? res.data : inv);
    } catch {
      setDetailInvoice(inv);
    }
    setDetailOpen(true);
  };

  const handleDownloadPdf = async (inv: ClientInvoice) => {
    try {
      const blob = await clientInvoiceGenerationService.downloadPdf(inv.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${inv.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setSuccessMsg(null);
      setInvoicesError(e?.message || 'Failed to download invoice PDF.');
    }
  };

  return (
    <PageLayout>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={700}>Client Invoices</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: 760 }}>
            Pick a client, load their completed shifts, tick the ones you want to bill, then
            generate the invoice. Each line item is priced as <strong>client base rate ×
            ClientCharge segment percentage</strong> from{' '}
            <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => navigate('/billing/rates')}>Billing Rates</Box>.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/billing/rates')}
        >
          Manage Rates
        </Button>
      </Box>

      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>{successMsg}</Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Client &amp; Date Range</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <Autocomplete
              size="small"
              options={clients}
              loading={clientsLoading}
              value={selectedClient}
              onChange={(_, v) => setSelectedClient(v)}
              getOptionLabel={clientDisplayName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search client..." />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField label="Start Date" type="date" value={startDate}
              onChange={e => setStartDate(e.target.value)} fullWidth size="small"
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField label="End Date" type="date" value={endDate}
              onChange={e => setEndDate(e.target.value)} fullWidth size="small"
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControlLabel
              control={<Switch checked={includeBilled} onChange={e => setIncludeBilled(e.target.checked)} size="small" />}
              label="Show billed" />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button variant="contained"
              startIcon={shiftsLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={loadShifts} disabled={shiftsLoading || !selectedClient} fullWidth>
              Load Shifts
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {shiftsError && <Alert severity="error" sx={{ mb: 2 }}>{shiftsError}</Alert>}

      {selectedClient && (
        <Paper sx={{ p: 2.5, mb: 3, bgcolor: 'background.default' }} variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Rates for {clientDisplayName(selectedClient)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                These rates will apply when generating invoices below.
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Service Type</InputLabel>
              <Select value={serviceType} onChange={e => setServiceType(e.target.value)} label="Service Type">
                {SERVICE_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{SERVICE_TYPE_LABELS[t] ?? t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Base Rate</Typography>
                {activeBaseRate ? (
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {fmtCurrency(activeBaseRate.baseHourlyRate)}<Typography component="span" variant="body2" color="text.secondary"> / hour</Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {SERVICE_TYPE_LABELS[serviceType] ?? serviceType} · since {fmtDate(activeBaseRate.effectiveFrom)}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="warning.main" fontWeight={600}>
                      No base rate configured
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add one in Billing Rates → Client Rates before invoicing.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Active ClientCharge Segments {ratesLoading && <CircularProgress size={10} sx={{ ml: 1 }} />}
                </Typography>
                {segmentsForServiceType.length === 0 ? (
                  <Typography variant="body2" color="warning.main">
                    No active ClientCharge segments. Configure them in Billing Rates → Rate Structure.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {segmentsForServiceType
                      .slice()
                      .sort((a, b) => a.dayType.localeCompare(b.dayType) || a.timeStartMinutes - b.timeStartMinutes)
                      .map(seg => (
                        <Tooltip
                          key={seg.id}
                          title={
                            activeBaseRate
                              ? `${fmtCurrency(activeBaseRate.baseHourlyRate)} × ${seg.percentage}% = ${fmtCurrency(activeBaseRate.baseHourlyRate * seg.percentage / 100)}/hr`
                              : `${seg.percentage}% loading`
                          }
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${seg.dayType} ${minutesToTime(seg.timeStartMinutes)}–${minutesToTime(seg.timeEndMinutes)} · ${seg.percentage}%`}
                          />
                        </Tooltip>
                      ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {shifts.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Completed Shifts ({shifts.length} total, {unbilledCount} invoiceable)
            </Typography>
            {selectedShiftIds.size > 0 && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedShiftIds.size} selected &middot; {totalSelectedHours.toFixed(2)} hrs
                  {totalSelectedAmount > 0 && <> &middot; est. {fmtCurrency(totalSelectedAmount)}</>}
                </Typography>
                <Button variant="contained" color="primary" startIcon={<ReceiptIcon />} onClick={openGenerateDialog}>
                  Generate Invoice
                </Button>
              </Box>
            )}
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedShiftIds.size > 0 && selectedShiftIds.size < unbilledCount}
                      checked={unbilledCount > 0 && selectedShiftIds.size === unbilledCount}
                      onChange={handleSelectAll} />
                  </TableCell>
                  <TableCell>Shift Date</TableCell>
                  <TableCell>Staff</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Est. Amount</TableCell>
                  <TableCell>Day Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.map(shift => {
                  const isSelected = selectedShiftIds.has(shift.shiftId);
                  const startDisplay = shift.actualStartTime ?? shift.startDateTime;
                  const endDisplay = shift.actualEndTime ?? shift.endDateTime;
                  return (
                    <TableRow key={shift.shiftId} hover selected={isSelected}
                      onClick={() => !shift.isBilled && handleToggle(shift.shiftId)}
                      sx={{ cursor: shift.isBilled ? 'default' : 'pointer', opacity: shift.isBilled ? 0.6 : 1 }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} disabled={shift.isBilled}
                          onChange={() => handleToggle(shift.shiftId)}
                          onClick={e => e.stopPropagation()} />
                      </TableCell>
                      <TableCell>{fmtDate(shift.shiftDate)}</TableCell>
                      <TableCell>{shift.staffName || `Staff #${shift.staffId}`}</TableCell>
                      <TableCell>{fmtTime(startDisplay)}</TableCell>
                      <TableCell>{fmtTime(endDisplay)}</TableCell>
                      <TableCell align="right">{Number(shift.totalHours).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const est = shiftEstimates.get(shift.shiftId);
                          if (!est || (est.total <= 0 && est.segments.length === 0)) {
                            const reason = est?.warning
                              ?? (est && est.uncoveredHours > 0
                                ? `${est.uncoveredHours.toFixed(2)}h not covered by any active ClientCharge ${getDayType(shift.shiftDate)} segment.`
                                : (shift.rateConfigError
                                  ?? (!activeBaseRate
                                    ? 'No active base rate for this client / service type. Configure one in Billing Rates → Client Rates.'
                                    : 'Rate not configured')));
                            return (
                              <Tooltip title={reason}>
                                <Typography variant="body2" color="error.main" sx={{ cursor: 'help' }}>
                                  Rate not configured
                                </Typography>
                              </Tooltip>
                            );
                          }
                          const segs = est.segments;
                          const hourlySegs = segs.filter(s => s.kind === 'Hourly' || !s.kind);
                          const hourlyRates = hourlySegs.map(s => s.rate);
                          const hasSleepover = segs.some(s => s.kind === 'Sleepover');
                          const hasTravel = segs.some(s => s.kind === 'Travel');
                          const minRate = hourlyRates.length ? Math.min(...hourlyRates) : 0;
                          const maxRate = hourlyRates.length ? Math.max(...hourlyRates) : 0;
                          const summary: string[] = [];
                          if (hasSleepover) summary.push('Sleepover');
                          if (hourlySegs.length === 1) {
                            summary.push(`${fmtCurrency(hourlySegs[0].rate)}/hr · ${hourlySegs[0].name}`);
                          } else if (hourlySegs.length > 1) {
                            summary.push(`${fmtCurrency(minRate)}–${fmtCurrency(maxRate)}/hr · ${hourlySegs.length} segments`);
                          }
                          if (hasTravel) summary.push('+ travel');
                          return (
                            <Tooltip
                              title={
                                <Box sx={{ p: 0.5 }}>
                                  {activeBaseRate && hourlySegs.length > 0 && (
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                      Base {fmtCurrency(activeBaseRate.baseHourlyRate)}/hr
                                    </Typography>
                                  )}
                                  {segs.map((s, i) => (
                                    <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                      {s.name}: {s.kind === 'Hourly' || !s.kind
                                        ? `${s.hours.toFixed(2)}h × ${fmtCurrency(s.rate)}/hr`
                                        : s.kind === 'Travel'
                                          ? `${s.hours.toFixed(2)}km × ${fmtCurrency(s.rate)}/km`
                                          : `1 night × ${fmtCurrency(s.rate)}`} = {fmtCurrency(s.amount)}
                                    </Typography>
                                  ))}
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                                    Total: {fmtCurrency(est.total)}
                                  </Typography>
                                  {est.uncoveredHours > 0 && (
                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                      {est.uncoveredHours.toFixed(2)}h uncovered by active segments
                                    </Typography>
                                  )}
                                  {est.warning && (
                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                      {est.warning}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            >
                              <Box sx={{ display: 'inline-block', cursor: 'help' }}>
                                <Typography variant="body2">{fmtCurrency(est.total)}</Typography>
                                {summary.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {summary.join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{getDayType(shift.shiftDate)}</TableCell>
                      <TableCell>
                        {shift.isBilled
                          ? <Chip label={shift.invoiceNumber ? `On ${shift.invoiceNumber}` : 'Billed'}
                              size="small" color="success" icon={<CheckCircleIcon />} />
                          : <Chip label={shift.status || 'Ready'} size="small" color="default" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {currentClientId !== null && (
        <Paper>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">
              Invoices for {selectedClient ? clientDisplayName(selectedClient) : `Client #${currentClientId}`}
            </Typography>
          </Box>
          <Divider />
          {invoicesLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : invoicesError ? (
            <Alert severity="error" sx={{ m: 2 }}>{invoicesError}</Alert>
          ) : invoices.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No invoices found for this client.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(inv => (
                    <TableRow key={inv.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{inv.invoiceNumber}</Typography></TableCell>
                      <TableCell>{fmtDate(inv.periodStart)} &ndash; {fmtDate(inv.periodEnd)}</TableCell>
                      <TableCell>{fmtDate(inv.dueDate)}</TableCell>
                      <TableCell align="right">{fmtCurrency(inv.totalAmount)}</TableCell>
                      <TableCell align="right">{fmtCurrency(inv.balanceAmount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={INVOICE_STATUS_LABELS[inv.status] ?? inv.statusText}
                          size="small"
                          color={STATUS_COLORS[inv.status] ?? 'default'} />
                      </TableCell>
                      <TableCell>{fmtDate(inv.createdAt)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View line items">
                          <IconButton size="small" onClick={() => openDetail(inv)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton size="small" onClick={() => handleDownloadPdf(inv)}>
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update status">
                          <IconButton size="small" onClick={() => openStatusDialog(inv)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete invoice">
                          <IconButton size="small" color="error" onClick={() => openDeleteDialog(inv.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          {generateError && <Alert severity="error" sx={{ mb: 2 }}>{generateError}</Alert>}
          {!activeBaseRate && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No active base rate found for {SERVICE_TYPE_LABELS[serviceType] ?? serviceType}.
              The invoice will fail to generate. Configure one in Billing Rates → Client Rates.
            </Alert>
          )}
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">Service Type &amp; Rate</Typography>
              <Typography variant="body2" fontWeight={600}>
                {SERVICE_TYPE_LABELS[serviceType] ?? serviceType}
                {activeBaseRate && <> &middot; {fmtCurrency(activeBaseRate.baseHourlyRate)} base/hr</>}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Change the service type from the rate panel above the shifts table.
              </Typography>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Service Type</InputLabel>
              <Select value={serviceType} onChange={e => setServiceType(e.target.value)} label="Service Type">
                {SERVICE_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{SERVICE_TYPE_LABELS[t] ?? t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Due Date" type="date" value={dueDate}
              onChange={e => setDueDate(e.target.value)} fullWidth size="small"
              InputLabelProps={{ shrink: true }} />
            <TextField label="Notes (optional)" value={notes}
              onChange={e => setNotes(e.target.value)} multiline rows={3} fullWidth size="small" />
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{selectedShiftIds.size}</strong> shifts selected
                {' \u00b7 '}<strong>{totalSelectedHours.toFixed(2)}</strong> total hours
                {totalSelectedAmount > 0 && <>{' \u00b7 est. '}<strong>{fmtCurrency(totalSelectedAmount)}</strong></>}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generating}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerate}
            disabled={generating || selectedShiftIds.size === 0}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Invoice {detailInvoice?.invoiceNumber}</DialogTitle>
        <DialogContent>
          {detailInvoice && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">Client</Typography>
                  <Typography>{detailInvoice.clientName || `Client #${detailInvoice.clientId}`}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">Period</Typography>
                  <Typography>{fmtDate(detailInvoice.periodStart)} &ndash; {fmtDate(detailInvoice.periodEnd)}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography>{fmtCurrency(detailInvoice.subTotal)}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="body2" color="text.secondary">GST</Typography>
                  <Typography>{fmtCurrency(detailInvoice.taxAmount)}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="body2" color="text.secondary">Total</Typography>
                  <Typography fontWeight={700}>{fmtCurrency(detailInvoice.totalAmount)}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Line Items</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty (hrs)</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailInvoice.lineItems ?? []).map(item => (
                      <TableRow key={item.id}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{Number(item.quantity).toFixed(2)}</TableCell>
                        <TableCell align="right">{fmtCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{fmtCurrency(item.totalPrice)}</TableCell>
                      </TableRow>
                    ))}
                    {(detailInvoice.lineItems ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">No line items</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusOpen} onClose={() => setStatusOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Invoice Status</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={newStatus} onChange={e => setNewStatus(Number(e.target.value))} label="Status">
                {Object.entries(INVOICE_STATUS_LABELS).map(([val, label]) => (
                  <MenuItem key={val} value={Number(val)}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusOpen(false)} disabled={statusUpdating}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusUpdate} disabled={statusUpdating}
            startIcon={statusUpdating ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete the invoice. The linked shifts will become available to invoice again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageLayout>
  );
};

export default ClientInvoicesPage;
